import React, { ReactNode } from 'react';

import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { IS_INDIAN_EXCHANGE } from 'constants/constants';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { navigateTo } from 'helpers/url';
import { noop } from 'helpers/utils';
import i18n from 'i18n/config';
import { isUserIndianSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import AadharIcon from 'styles/assets/general/AadharCard.svg';
import {
  UserTick,
  SelfieIcon,
  PhoneIcon,
  PanCardIcon,
  CrossIcon,
} from 'styles/assets/icons';
import BankImage from 'styles/assets/UserJourneyCards/bank.png';
import TradeImage from 'styles/assets/UserJourneyCards/barChart.png';
import CalenderImage from 'styles/assets/UserJourneyCards/calendar.png';
import DepositImage from 'styles/assets/UserJourneyCards/deposit.png';
import PurpleCalendarImage from 'styles/assets/UserJourneyCards/purple_calendar.png';
import MobileImage from 'styles/assets/UserJourneyCards/smartphone.png';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton/DeltaButton';
import IconButton from 'UIKit/IconButton';

import styles from './commonCardStyles.module.scss';
import Steps from './Steps/Steps';
import {
  CARD_LEVELS_TEXT,
  COMPLETE_30_DAY_CARD_CONSTANTS,
  USER_JOURNEY_CARDS_LEVEL,
} from './UserJourneyCard.constants';
import { CardProps } from './UserJourneyCard.types';

// #region  CommonCardComponents

interface CardGridProps {
  left?: ReactNode;
  right?: ReactNode;
  classes?: {
    left?: string;
    right?: string;
    root?: string;
  };
}

const CardGrid = ({ left = null, right = null, classes }: CardGridProps) => {
  return (
    <Box className={cx(styles.cardContainer, classes?.root)}>
      <Box className={cx(styles.leftItem, classes?.left)}>{left}</Box>
      <Box className={cx(styles.rightItem, classes?.right)}>{right}</Box>
    </Box>
  );
};

const CardRightSectionImage = ({
  imageSrc,
  className,
}: {
  imageSrc: string;
  className?: string;
}) => {
  return (
    <Box className={cx(styles.imageContainer, className)}>
      <img src={imageSrc} alt="mobile" />
    </Box>
  );
};
interface CardRightSection {
  loading: boolean;
  onClose: () => void;
  heading: string;
  description: string;
  image: ReturnType<typeof CardRightSectionImage>;
}

const CardsRightSection = ({
  loading,
  children,
  onClose,
  description,
  heading,
  image,
}: React.PropsWithChildren<CardRightSection>) => {
  return (
    <Box className={styles.cardRightContent}>
      <IconButton
        data-testid="cardRightCloseIcon"
        icon={<CrossIcon />}
        onClick={onClose}
        onKeyDown={noop}
        className={styles.closeIcon}
        disabled={loading}
      />
      {image}
      <Box flex="flex" flexDir="column">
        <Box
          fontSize="sm4"
          textColor="primary"
          className={styles.heading}
          fontType="semi-bold"
        >
          {heading}
        </Box>
        <Box className={styles.content} fontSize="sm4" textColor="secondary">
          {description}
        </Box>
        {children}
      </Box>
    </Box>
  );
};

interface CardLeftSectionProps {
  activeLevel: number;
  heading: string;
}
const CardLeftSection = ({ activeLevel, heading }: CardLeftSectionProps) => {
  const { t } = useTranslation(['markets']);

  return (
    <Box className={styles.cardLeftContent} flex="flex" flexDir="column">
      <Box className={styles.cardHeading} textColor="primary" fontType="bold">
        {heading}
      </Box>
      <Box className={styles.stepsHeading} fontSize="sm4">
        {t('markets:userJourneyCards.nextSteps')}:
      </Box>
      <Steps activeLevel={activeLevel} levels={CARD_LEVELS_TEXT} />
    </Box>
  );
};

// #endregion

// #region DoKycCard

interface DoKycCardProps extends CardProps {}

const NON_INDIAN_REQUIREMENTS_LIST = [
  {
    id: 1,
    icon: <UserTick className={styles.kycListIcon} />,
    text: i18n.t('account:profile.governmentIssuedId'),
  },
  {
    id: 2,
    icon: <PhoneIcon className={styles.kycListIcon} />,
    text: i18n.t('account:profile.phoneNoVerification'),
  },
  {
    id: 3,
    icon: <SelfieIcon className={styles.kycListIcon} />,
    text: i18n.t('account:profile.selfieVerification'),
  },
];

const INDIAN_REQUIREMENT_LIST = [
  {
    id: 0,
    icon: <PhoneIcon className={styles.kycListIcon} />,
    text: IS_INDIAN_EXCHANGE
      ? i18n.t('auth:phoneVerification')
      : i18n.t('account:profile.phoneNoVerification'),
    order: 1,
  },
  {
    id: 1,
    icon: <img
      className={styles.kycListIcon}
      src={AadharIcon}
      alt="aadharIcon"
      data-palette="INDIAN_REQUIREMENT_LIST" />,
    text: i18n.t('account:profile.aadhar_card'),
    order: IS_INDIAN_EXCHANGE ? 4 : 2,
  },
  {
    id: 2,
    icon: <PanCardIcon className={cx(styles.panCardIcon, styles.kycListIcon)} />,
    text: i18n.t('account:profile.pan_card'),
    order: 3,
  },
  {
    id: 3,
    icon: <SelfieIcon className={styles.kycListIcon} />,
    text: i18n.t('account:profile.selfieVerification'),
    order: IS_INDIAN_EXCHANGE ? 2 : 4,
  },
];

const getKYCRequirementsList = (indianUser: boolean) => {
  if (IS_INDIAN_EXCHANGE)
    return INDIAN_REQUIREMENT_LIST.sort((req1, req2) => req1.order - req2.order);
  return indianUser ? INDIAN_REQUIREMENT_LIST : NON_INDIAN_REQUIREMENTS_LIST;
};

const DO_KYC_STATIC_TEXT = {
  DESCRIPTION: i18n.t('markets:userJourneyCards.completeIdentityVerificationDesc'),
  globalImportedDescription: i18n.t(
    'markets:userJourneyCards.completeIdentityVerificationDescImport'
  ),
};

const DoKycCardRight = ({ loading, onCloseRequest }: CardProps) => {
  const { t } = useTranslation(['markets', 'account']);

  const indianUser = useAppSelector(isUserIndianSelector);

  const requirementsList = getKYCRequirementsList(indianUser);

  const history = useHistory();

  const handleButtonCLick = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.POPUP_CARD_CLICKED, {
      card_name: 'KYC Card',
    });
    onCloseRequest().then(() => {
      history.push(routes.account.identityVerification);
    });
  };

  return (
    <CardsRightSection
      description={DO_KYC_STATIC_TEXT.DESCRIPTION}
      heading={t('markets:userJourneyCards.completeIdentityVerification')}
      image={
        <CardRightSectionImage
          imageSrc={MobileImage}
          className={styles.kycImageContainer}
        />
      }
      loading={loading}
      onClose={onCloseRequest}
      data-testid="kyc-card-section-right"
    >
      <Box className={styles.kycListContainer}>
        <Box as="h5" textColor="primary">
          {t('account:profile.requirements')}
        </Box>
        <Box as="ul">
          {requirementsList.map(({ id, text, icon }) => {
            return (
              <Box
                as="li"
                className={styles.listItem}
                flex="inline"
                gap={8}
                key={id}
                textColor="secondary"
                fullWidth
              >
                {icon} <Box as="span">{text}</Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      <DeltaButton
        data-testid="verifyButton"
        fullWidth
        onClick={handleButtonCLick}
        loading={loading}
      >
        {t('markets:userJourneyCards.verifyNow')}
      </DeltaButton>
    </CardsRightSection>
  );
};

const DoKycCard = ({ loading, onCloseRequest }: DoKycCardProps) => {
  const { t } = useTranslation(['markets']);

  return (
    <CardGrid
      left={
        <CardLeftSection
          activeLevel={USER_JOURNEY_CARDS_LEVEL.kyc as number}
          heading={t('markets:userJourneyCards.emailVerificationSuccessFul')}
        />
      }
      classes={{ left: styles.cardLeft }}
      right={<DoKycCardRight loading={loading} onCloseRequest={onCloseRequest} />}
    />
  );
};

// #endregion

// #region  DoAddBankCard

const ADD_BANK_CARD_STATIC_TEXT = {
  HEADING: i18n.t('markets:userJourneyCards.addYourBankAccount'),
  DESCRIPTION: i18n.t('markets:userJourneyCards.addBankAccountDescription'),
  ADD_BANK_NOW_BUTTON: i18n.t('markets:userJourneyCards.addBankNow'),
};

interface DoAddBankCardProps extends CardProps {}

const DoAddBankCardRight = ({ loading, onCloseRequest }: DoAddBankCardProps) => {
  const history = useHistory();

  const handleButtonClick = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.POPUP_CARD_CLICKED, {
      card_name: 'Add Bank Card',
    });

    onCloseRequest().then(() => {
      history.push(routes.account.bankDetails);
    });
  };

  return (
    <CardsRightSection
      heading={ADD_BANK_CARD_STATIC_TEXT.HEADING}
      image={
        <CardRightSectionImage
          imageSrc={BankImage}
          className={styles.doAddBankImageContainer}
        />
      }
      loading={loading}
      onClose={onCloseRequest}
      description={ADD_BANK_CARD_STATIC_TEXT.DESCRIPTION}
    >
      <DeltaButton
        data-testid="addBankNow"
        onClick={handleButtonClick}
        loading={loading}
        className={styles.doAddBankbutton}
      >
        {ADD_BANK_CARD_STATIC_TEXT.ADD_BANK_NOW_BUTTON}
      </DeltaButton>
    </CardsRightSection>
  );
};

const DoAddBankCard = ({ loading, onCloseRequest }: DoDepositCardProps) => {
  const { t } = useTranslation(['markets']);

  return (
    <CardGrid
      left={
        <CardLeftSection
          activeLevel={USER_JOURNEY_CARDS_LEVEL.add_bank as number}
          heading={t('markets:userJourneyCards.identityVerificationSuccessFul')}
        />
      }
      right={<DoAddBankCardRight loading={loading} onCloseRequest={onCloseRequest} />}
    />
  );
};

// #endregion

// #region  DoDepositCard

const DO_DEPOSIT_CARD_STATIC_TEXT = {
  LEFT_HEADING: IS_INDIAN_EXCHANGE
    ? i18n.t('markets:userJourneyCards.bankAdditionSuccessful')
    : i18n.t('markets:userJourneyCards.identityVerificationSuccessFul'),
  HEADING: IS_INDIAN_EXCHANGE
    ? i18n.t('markets:userJourneyCards.makeFirstDepositWithUpi')
    : i18n.t('markets:userJourneyCards.depositBuyCrypto'),
  DESCRIPTION: IS_INDIAN_EXCHANGE
    ? i18n.t('markets:userJourneyCards.makeYourFirstTrade')
    : i18n.t('markets:userJourneyCards.depositBuyCryptoDescription'),
  DEPOSIT_NOW_BUTTON: IS_INDIAN_EXCHANGE
    ? i18n.t('markets:userJourneyCards.depositNow')
    : i18n.t('markets:userJourneyCards.depositbuyNow'),
};

interface DoDepositCardProps extends CardProps {}

const DoDepositCardRight = ({ loading, onCloseRequest }: DoDepositCardProps) => {
  const indianUser = useAppSelector(isUserIndianSelector);

  const history = useHistory();

  const handleButtonClick = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.POPUP_CARD_CLICKED, {
      card_name: 'Deposit Card',
    });

    onCloseRequest().then(() => {
      let redirectURL = routes.account.deposit;
      if (!IS_INDIAN_EXCHANGE && indianUser) redirectURL = routes.account.buy_crypto;
      history.push(redirectURL);
    });
  };

  return (
    <CardsRightSection
      heading={DO_DEPOSIT_CARD_STATIC_TEXT.HEADING}
      image={
        <CardRightSectionImage
          imageSrc={DepositImage}
          className={styles.doDepositImageContainer}
        />
      }
      loading={loading}
      onClose={onCloseRequest}
      description={DO_DEPOSIT_CARD_STATIC_TEXT.DESCRIPTION}
    >
      <DeltaButton
        data-testid="depositNow"
        onClick={handleButtonClick}
        loading={loading}
        className={cx(styles.doDepositbutton, {
          [styles.centerDepositButton]: IS_INDIAN_EXCHANGE,
        })}
      >
        {DO_DEPOSIT_CARD_STATIC_TEXT.DEPOSIT_NOW_BUTTON}
      </DeltaButton>
    </CardsRightSection>
  );
};

const DoDepositCard = ({ loading, onCloseRequest }: DoDepositCardProps) => {
  return (
    <CardGrid
      left={
        <CardLeftSection
          activeLevel={USER_JOURNEY_CARDS_LEVEL.deposit as number}
          heading={DO_DEPOSIT_CARD_STATIC_TEXT.LEFT_HEADING}
        />
      }
      right={<DoDepositCardRight loading={loading} onCloseRequest={onCloseRequest} />}
    />
  );
};

// #endregion

// #region  DoTradeCard

interface DoTradeCardProps extends CardProps {}

const DoTradeCardRight = ({ loading, onCloseRequest }: DoTradeCardProps) => {
  const { t } = useTranslation(['markets', 'account']);

  const history = useHistory();

  const handleButtonClick = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.POPUP_CARD_CLICKED, {
      card_name: 'Trade Card',
    });

    onCloseRequest().then(() => {
      history.push(routes.options.trade);
    });
  };

  return (
    <CardsRightSection
      heading={t('markets:userJourneyCards.makeFirstTrade')}
      description={t('markets:userJourneyCards.makeFirstTradeDescription')}
      image={
        <CardRightSectionImage
          imageSrc={TradeImage}
          className={styles.tradeImageContainer}
        />
      }
      loading={loading}
      onClose={onCloseRequest}
    >
      <DeltaButton
        data-testid="depositNow"
        onClick={handleButtonClick}
        className={styles.tradeButton}
        loading={loading}
      >
        {t('markets:userJourneyCards.tradeNow')}
      </DeltaButton>
    </CardsRightSection>
  );
};

const DoTradeCard = ({ loading, onCloseRequest }: DoTradeCardProps) => {
  const { t } = useTranslation(['markets']);

  return (
    <CardGrid
      left={
        <CardLeftSection
          activeLevel={USER_JOURNEY_CARDS_LEVEL.trade as number}
          heading={t('markets:userJourneyCards.depositSuccessFul')}
        />
      }
      right={<DoTradeCardRight loading={loading} onCloseRequest={onCloseRequest} />}
    />
  );
};

// #endregion

// #region Complete30Day

const THIRTY_DAY_CARD_STATIC_TEXT = {
  LEFT_HEADING: IS_INDIAN_EXCHANGE
    ? i18n.t('markets:userJourneyCards.firstDepositSuccessFul')
    : i18n.t('markets:userJourneyCards.firstTradeSuccessFul'),
  HEADING: IS_INDIAN_EXCHANGE
    ? i18n.t('markets:userJourneyCards.completeTheWelcomeOffer')
    : i18n.t('markets:userJourneyCards.complete30DayOffer'),
  DESCRIPTION: IS_INDIAN_EXCHANGE
    ? i18n.t('markets:userJourneyCards.complete30DayOfferDescription_india', {
        amount: COMPLETE_30_DAY_CARD_CONSTANTS.BONUS_AMOUNT,
      })
    : i18n.t('markets:userJourneyCards.complete30DayOfferDescription', {
        amount: COMPLETE_30_DAY_CARD_CONSTANTS.BONUS_AMOUNT,
        days: COMPLETE_30_DAY_CARD_CONSTANTS.DAYS,
      }),
  BUTTON_TEXT: IS_INDIAN_EXCHANGE
    ? i18n.t('markets:userJourneyCards.tradeNow')
    : i18n.t('markets:userJourneyCards.checkNow'),
};

interface Complete30DayCardProps extends CardProps {}

const Complete30DayCardRight = ({ loading, onCloseRequest }: Complete30DayCardProps) => {
  const history = useHistory();

  const handleButtonClick = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.POPUP_CARD_CLICKED, {
      card_name: IS_INDIAN_EXCHANGE ? 'Welcome Card' : '30Day Card',
    });

    onCloseRequest().then(() => {
      if (IS_INDIAN_EXCHANGE) {
        history.push(routes.options.trade);
        return;
      }
      navigateTo(routes.offers, '_blank');
    });
  };
  return (
    <CardsRightSection
      heading={THIRTY_DAY_CARD_STATIC_TEXT.HEADING}
      description={THIRTY_DAY_CARD_STATIC_TEXT.DESCRIPTION}
      image={
        <CardRightSectionImage
          imageSrc={IS_INDIAN_EXCHANGE ? PurpleCalendarImage : CalenderImage}
          className={
            IS_INDIAN_EXCHANGE
              ? styles.complete30ImageContainerIndia
              : styles.complete30ImageContainer
          }
        />
      }
      loading={loading}
      onClose={onCloseRequest}
    >
      <DeltaButton
        data-testid="tradeNow"
        onClick={handleButtonClick}
        loading={loading}
        className={cx(styles.complete30button, {
          [styles.center30button]: IS_INDIAN_EXCHANGE,
        })}
      >
        {THIRTY_DAY_CARD_STATIC_TEXT.BUTTON_TEXT}
      </DeltaButton>
    </CardsRightSection>
  );
};

const Complete30DayCard = ({ loading, onCloseRequest }: Complete30DayCardProps) => {
  return (
    <CardGrid
      left={
        <CardLeftSection
          activeLevel={USER_JOURNEY_CARDS_LEVEL['30_days_journey'] as number}
          heading={THIRTY_DAY_CARD_STATIC_TEXT.LEFT_HEADING}
        />
      }
      right={<Complete30DayCardRight loading={loading} onCloseRequest={onCloseRequest} />}
    />
  );
};

// #endregion

export { DoKycCard, DoAddBankCard, DoDepositCard, DoTradeCard, Complete30DayCard };
